import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { GroupEntity } from '@/types';

/**
 * Group Context Store(R45 / Beta v0.0.1)
 * -----------------------------------------------------------------------------
 * 對齊 docs/40 §2 + R45 8-agent panel synthesis:
 *
 * - `currentGroupId`:每個 mutation(POST/PUT/PATCH/DELETE)自動帶進 `X-Current-Group`
 *   header(`httpApiClient.getCurrentGroupId` injection)
 * - `availableGroups`:從 `GET /api/groups` 拉回的 membership list(R45 Phase 1
 *   `useGroupBootstrap` 寫入);GroupSwitcher / 切 group 切換邏輯都從這讀
 * - localStorage SoT(persist key 直接 `current_group_id` 對齊 spec §2.2;BE / ops
 *   同步 reference,FE 不可改 key 名)
 *
 * 為什麼用 localStorage 而非 sessionStorage(像 `authStore`):
 *   - spec §2.2 明文 localStorage(跨 tab 一致比較不撞 — 多 tab 不同 group 是邊緣 case)
 *   - 切 group 是 user 顯式動作,reload 後該記得不撞 personal fallback
 *   - 但 `current_group_id` 並非「身份」憑證(cookie 是),localStorage 過期 / 髒值
 *     由 BE 二次驗 + L1.5 GROUP_FORBIDDEN 把關
 *
 * 安全:`availableGroups` 永遠不持久化(每次 boot 重 fetch);避免 stale data 跨 user
 * 殘留(R45 Security Engineer panel 規則「no group-scoped key without prefix」)。
 */
export interface GroupContextState {
  currentGroupId: string | null;
  availableGroups: GroupEntity[];
  /**
   * R48.10 — bootstrap race fix。
   * - `null`:尚未啟動(初始)
   * - `true`:`useGroupBootstrap` 在跑(/me / /groups 任一 in-flight)
   * - `false`:bootstrap 結束(成功 or fallback 過 or 全失敗 — RequireAuth 可放行)
   *
   * RequireAuth 用這個 gate 渲染 children — 避免 child page fetch L1.5 endpoint
   * 時 `X-Current-Group` 還沒寫進 store 撞 400 `CURRENT_GROUP_REQUIRED`。
   */
  bootstrapping: boolean | null;
  setCurrentGroupId: (gid: string | null) => void;
  setAvailableGroups: (groups: GroupEntity[]) => void;
  setBootstrapping: (v: boolean) => void;
  clear: () => void;
}

const initialState = {
  currentGroupId: null as string | null,
  availableGroups: [] as GroupEntity[],
  bootstrapping: null as boolean | null,
};

export const useGroupContextStore = create<GroupContextState>()(
  persist(
    (set) => ({
      ...initialState,
      setCurrentGroupId: (currentGroupId) => set({ currentGroupId }),
      setAvailableGroups: (availableGroups) => set({ availableGroups }),
      setBootstrapping: (bootstrapping) => set({ bootstrapping }),
      clear: () => set(initialState),
    }),
    {
      name: 'current_group_id',
      storage: createJSONStorage(() => localStorage),
      /** 只持久化 currentGroupId;availableGroups 每次 boot 重新 fetch。 */
      partialize: (state) => ({ currentGroupId: state.currentGroupId }),
    },
  ),
);

/**
 * Imperative getter for non-React contexts(httpApiClient header injection)。
 *
 * httpClient 不 import React hook,但讀 zustand `getState()` 是合法慣例
 * (對齊既有 `useAuthStore.getState().clear()` 在 `onAuthLost` 內用法)。
 */
export function getCurrentGroupId(): string | null {
  return useGroupContextStore.getState().currentGroupId;
}
